import { useEffect, useState } from 'react';
import { fetchWithProgress } from '../download';
import { decryptFile, type FileSecret } from './crypto';
import type { OpenedE2EE } from './session';

// Encrypted photos, videos, voice notes and files (M28, D59): fetched as
// ciphertext, decrypted on the device, shown from a blob URL.

/** The secrets an opened message carries for its file and its preview. */
export function mediaSecrets(message: {
  e2ee?: OpenedE2EE;
  metadata?: Record<string, unknown> | null;
}): { file?: FileSecret; thumb?: FileSecret; mimeType?: string } {
  const meta = message.e2ee?.envelope?.meta;
  if (!meta || meta.plainFile) return {};
  return { file: meta.file, thumb: meta.thumb, mimeType: meta.mimeType };
}

// One decrypted copy per file for the whole session, keyed by its secret.
const decrypted = new Map<string, Promise<string>>();

export function decryptedUrl(
  url: string,
  secret: FileSecret,
  mimeType = ''
): Promise<string> {
  const cached = decrypted.get(secret.key);
  if (cached) return cached;
  const promise = fetchDecryptedBlob(url, secret, mimeType).then((blob) =>
    URL.createObjectURL(blob)
  );
  promise.catch(() => decrypted.delete(secret.key));
  decrypted.set(secret.key, promise);
  return promise;
}

export async function fetchDecryptedBlob(
  url: string,
  secret: FileSecret,
  mimeType = '',
  options: {
    signal?: AbortSignal;
    sizeHint?: number;
    onProgress?: (loaded: number, total: number) => void;
  } = {}
): Promise<Blob> {
  const sealed = await fetchWithProgress(url, options);
  return decryptFile(await sealed.arrayBuffer(), secret, mimeType);
}

/**
 * A URL to show: the file itself when it isn't encrypted, else a decrypted
 * blob URL once ready (undefined until then).
 */
export function useMediaUrl(
  url: string | undefined,
  secret: FileSecret | undefined,
  mimeType?: string
): { src: string | undefined; failed: boolean } {
  const [state, setState] = useState<{
    src: string | undefined;
    failed: boolean;
  }>(() => ({
    src: secret ? undefined : url,
    failed: false,
  }));
  useEffect(() => {
    if (!url) {
      setState({ src: undefined, failed: false });
      return;
    }
    if (!secret) {
      setState({ src: url, failed: false });
      return;
    }
    let cancelled = false;
    setState({ src: undefined, failed: false });
    decryptedUrl(url, secret, mimeType)
      .then((src) => {
        if (!cancelled) setState({ src, failed: false });
      })
      .catch(() => {
        if (!cancelled) setState({ src: undefined, failed: true });
      });
    return () => {
      cancelled = true;
    };
    // The secret's key identifies the file; the signed URL changes over time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secret?.key, Boolean(url)]);
  return state;
}
